import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useAppointableCategories,
  useMutationDeleteSessionPack,
  useQueryActivityCategoryList,
  useSessionPack,
  useSessionPackDisable,
  useSessionPackEdit,
  useSessionPackEnable,
  useSessionPackList,
} from "@gymflow/api";
import { isMobile } from "@gymflow/common";
import { formatCurrency } from "@gymflow/helpers";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { copyToClipboard } from "apps/portal/src/helpers";
import { usePageSize, usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature, RouteLayout } from "apps/portal/src/routes";
import classNames from "classnames";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "usehooks-ts";

import { ModalContext, useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import {
  Badge,
  Button,
  CopyIcon,
  PaginatedSelect,
  ReactiveButton,
  Switch,
} from "../../atoms";
import { PaginatedTable } from "../../organisms/PaginatedTable";
import { ConfirmModal } from "../../templates";
import { SettingsContainer } from "../SettingsContainer";
import { CreditPackFormSidebarProviderContext } from "./CreditPackFormSidebarProvider";

const tabs = ["classPack", "appointmentPack"] as const;
const typeToString: { [key in (typeof tabs)[number]]: string } = {
  classPack: "Class Packs",
  appointmentPack: "Appointment Packs",
};
const filters: {
  [key in (typeof tabs)[number]]: {};
} = {
  classPack: {
    hasActivityCategory: true,
  },
  appointmentPack: {
    hasAppointableCategory: true,
  },
};
export function CreditPackTab() {
  const { createAbsoluteClubLink } = usePortalRoutes();
  const [currentPage, setCurrentPage] = useState(0);
  const [showingStatus, setShowingStatus] = useState<"ACTIVE" | "INACTIVE">(
    "ACTIVE",
  );
  const [showingType, setShowingType] =
    useState<(typeof tabs)[number]>("classPack");
  const [sort, setSort] = useState<SortingState>([]);

  const { api } = useGymflowModels();

  const { setModal, hide } = useContext(ModalContext);
  const { t } = useTranslation();
  const { club } = useClubSettings();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });

  const { data, isLoading } = useSessionPackList({
    api,
    opts: {
      page: currentPage,
      limit: pageSize,
      sort: sort?.[0]
        ? {
            field: sort[0].id === "card" ? "name" : sort[0].id,
            desc: sort[0].desc,
          }
        : { field: "createdDate", desc: true },
      extraParams: {
        status: showingStatus,
        ...filters[showingType],
      },
    },
  });

  const [editingCreditPackId, setEditingCreditPackId] = useState<number>();

  const { data: editing } = useSessionPack({
    api,
    sessionPackId: editingCreditPackId,
  });
  const { data: activityCategories } = useQueryActivityCategoryList({
    api,
    filter: {
      size: 100,
      page: 0,
    },
  });
  const { data: appointableCategories } = useAppointableCategories({
    api,
    paginationOption: {
      page: 0,
      size: 100,
    },
  });
  const { mutateAsync: editSessionPack } = useSessionPackEdit({ api });
  const { mutateAsync: mutateAsyncDeleteSessionPack } =
    useMutationDeleteSessionPack({ api });
  const { mutateAsync: enableSessionPack } = useSessionPackEnable({ api });
  const { mutateAsync: disableSessionPack } = useSessionPackDisable({ api });
  const { showCreditPackForm } = useContext(
    CreditPackFormSidebarProviderContext,
  );
  const showAlert = useCallback(() => {
    showCreditPackForm({
      currentCreditPack: editing ?? undefined,
      type: showingType,
      onClose: () => {
        setEditingCreditPackId(undefined);
      },
    });
  }, [editing, showingType, showCreditPackForm]);

  useEffect(() => {
    if (editingCreditPackId && editing) {
      showAlert();
    }
  }, [editing, editingCreditPackId, showAlert]);

  const actionButtons = useCallback(
    (item: number) => {
      return (
        <>
          <div
            className="text-primary-700 cursor-pointer text-sm font-semibold"
            onClick={() => {
              setEditingCreditPackId(item);
            }}
          >
            Edit
          </div>
          <div
            className="text-warning-600 cursor-pointer text-sm font-semibold"
            onClick={async () => {
              if (showingStatus === "ACTIVE") {
                await disableSessionPack(item);
              } else {
                await enableSessionPack(item);
              }
            }}
          >
            {showingStatus === "ACTIVE" ? "Disable" : "Enable"}
          </div>
          <div
            className="text-error-500 cursor-pointer text-sm font-semibold"
            onClick={() => {
              setModal(
                <ConfirmModal
                  title={t(
                    "page.settings.tab.sessions.table.actions.delete.confirmModal.title",
                  )}
                  type="danger"
                  onConfirm={async () => {
                    await mutateAsyncDeleteSessionPack({
                      sessionPackId: item,
                    });

                    hide();
                  }}
                  onCancel={() => {
                    hide();
                  }}
                >
                  {t(
                    "page.settings.tab.sessions.table.actions.delete.confirmModal.description",
                  )}
                </ConfirmModal>,
              );
            }}
          >
            {t("portal.common.delete")}
          </div>
        </>
      );
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableSessionPack, enableSessionPack, showingStatus],
  );
  const columnsDesktop = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof data>["content"][number]>();
    return [
      columnHelper.accessor("name", {}),
      columnHelper.accessor("price", {
        id: "price",
        cell: (cell) => {
          return formatCurrency(cell.getValue(), club.defaultCurrency);
        },
      }),
      columnHelper.accessor(
        (e) => ({
          sessionsIncluded: e.sessionsIncluded,
          sessionsUnlimited: e.sessionsUnlimited,
        }),
        {
          id: "sessionsIncluded",
          header: () => <div>Credits</div>,
          cell: (cell) => {
            return cell.getValue().sessionsUnlimited
              ? "∞"
              : `${cell.getValue().sessionsIncluded}`;
          },
        },
      ),
      ...(showingType === "classPack"
        ? [
            columnHelper.accessor("activityCategoryIdList", {
              header: "Class Category",
              cell: (cell) => {
                return (
                  <div className="flex flex-row gap-2 overflow-x-auto">
                    {cell.row.original.activityCategoryIdList?.map((id) => (
                      <Badge key={id}>
                        {
                          activityCategories?.content.find((e) => e.id === id)
                            ?.name
                        }
                      </Badge>
                    ))}
                  </div>
                );
              },
            }),
          ]
        : [
            columnHelper.accessor("appointableCategoryIdList", {
              header: "Class Category",
              cell: (cell) => {
                return (
                  <div className="flex flex-row gap-2 overflow-x-auto">
                    {cell.row.original.appointableCategoryIdList?.map((id) => (
                      <Badge key={id}>
                        {
                          appointableCategories?.content.find(
                            (e) => e.id === id,
                          )?.name
                        }
                      </Badge>
                    ))}
                  </div>
                );
              },
            }),
          ]),
      columnHelper.accessor(
        (e) => ({
          isPublic: e.isPublic,
          id: e.id,
        }),
        {
          id: "isPublic",
          header: () => <div>Available Online</div>,
          cell: (column) => {
            const value = column.getValue();
            return (
              <Switch
                value={!!value.isPublic}
                onChange={async (newValue) => {
                  await editSessionPack({
                    sessionPackId: value.id,
                    patchedFields: {
                      isPublic: newValue,
                    },
                  });
                }}
              />
            );
          },
        },
      ),
      columnHelper.accessor("id", {
        id: "creditPackLink",
        header: "Credit Pack Link",
        cell: (column) => (
          <ReactiveButton
            size="small"
            onClick={async () => {
              await copyToClipboard(
                createAbsoluteClubLink(
                  RouteLayout.Site,
                  RouteFeature.SiteBuySessionPackId.replace(
                    ":sessionPackType",
                    "all",
                  ).replace(":sessionPackId", column.getValue().toString()),
                ),
              );
            }}
            reactiveContent="Link Copied"
            className="mr-4 w-32"
            disabled={!column.row.original.isPublic}
            tooltip={
              column.row.original.isPublic
                ? undefined
                : "To enable the link the credit pack should be available online"
            }
          >
            <div className="flex w-full items-center gap-1">
              <CopyIcon pathClassName="stroke-gray-500" />
              Copy Link
            </div>
          </ReactiveButton>
        ),
      }),

      columnHelper.accessor("id", {
        header: () => <div>Actions</div>,
        cell: (column) => {
          const value = column.getValue();
          return (
            <div className="flex flex-row items-center gap-x-2">
              {actionButtons(value)}
            </div>
          );
        },
        enableSorting: false,
      }),
    ];
  }, [
    actionButtons,
    activityCategories?.content,
    appointableCategories?.content,
    createAbsoluteClubLink,
    editSessionPack,
    club.defaultCurrency,
    showingType,
  ]);
  const mobileColumns = useMemo(() => {
    const columnHelper =
      createColumnHelper<NonNullable<typeof data>["content"][number]>();
    return [
      columnHelper.accessor((e) => e, {
        id: "card",
        header: () => <div>Name</div>,
        cell: (column) => {
          const item = column.getValue();
          return (
            <div className="flex w-full flex-col justify-center gap-y-2 py-2">
              <div>
                <div className="text-lg font-semibold">{item.name}</div>
                <div className="text-sm text-gray-600">
                  {formatCurrency(item.price, club.defaultCurrency)}
                </div>
                <div className="text-sm text-gray-600">
                  {item.sessionsUnlimited ? "∞" : `${item.sessionsIncluded}`}{" "}
                  Credits
                </div>
              </div>
              <div className="flex flex-row items-center gap-x-3">
                {actionButtons(item.id)}
              </div>
            </div>
          );
        },
      }),
    ];
  }, [actionButtons, club.defaultCurrency]);

  const windowSize = useWindowSize({ debounceDelay: 100 });
  return (
    <SettingsContainer
      title="Credit Packs"
      subTitle="Create, Update and manage credit packs for bookings."
      actions={
        <>
          <PaginatedSelect
            className="bg-gray-0 h-11"
            placeholder="Enabled"
            value={[
              {
                label: "Enabled",
                value: "ACTIVE",
              },
              {
                label: "Disabled",
                value: "INACTIVE",
              },
            ].find((e) => e.value === showingStatus)}
            onChange={({ value }) => setShowingStatus(value)}
            loadOptions={async () => {
              return {
                options: [
                  {
                    label: "Enabled",
                    value: "ACTIVE",
                  },
                  {
                    label: "Disabled",
                    value: "INACTIVE",
                  },
                ],
              };
            }}
          />
          <Button
            intent="secondary"
            className="text-gray-0"
            onClick={() => {
              showAlert();
            }}
          >
            <div className="flex flex-row items-center gap-x-2">
              <FontAwesomeIcon icon={faPlus} />
              <div>Credit Pack</div>
            </div>
          </Button>
        </>
      }
    >
      <div className="flex flex-row gap-x-2 border-b border-gray-200">
        {tabs.map((e) => (
          <div
            key={e}
            onClick={() => setShowingType(e)}
            className={classNames(
              "max-w-min cursor-pointer flex-nowrap whitespace-nowrap px-1 pb-3 text-sm font-semibold text-gray-500",
              {
                "text-secondary-500 border-secondary-500 border-b-2 ":
                  showingType === e,
              },
            )}
          >
            {typeToString[e]}
          </div>
        ))}
      </div>
      <PaginatedTable
        tableProps={{
          data: data?.content ?? [],
          columns: isMobile(windowSize.width) ? mobileColumns : columnsDesktop,
          pageCount: data?.totalPages ?? 0,
          pageIndex: data?.number ?? 0,
          onSortingChange: setSort,
          sort: sort,
          isFetching: isLoading,
          pageSize: pageSize,
          tableContainerRef,
        }}
        hasNextPage={(data?.number ?? 0) < (data?.totalPages ?? 0) - 1}
        hasPreviousPage={(data?.number ?? 0) > 0}
        goToNextPage={() => {
          setCurrentPage(currentPage + 1);
        }}
        goToPreviousPage={() => {
          setCurrentPage(currentPage - 1);
        }}
      />
    </SettingsContainer>
  );
}
